import React, { FC, useMemo, useState } from 'react';

import { Form, Formik, FormikProps } from 'formik';

// components
import AampeTooltip from '../../../components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';
// constants
import ICONS from '../../../constants/icons';

import debounce from '../../../helpers/debounce';
// helpers
import {
    getAvailableAbbreviatedWeekDays,
    getConstraintsChanges,
    getInitialConstraintsValues,
    getIsOtherTimeSelected,
    getIsOtherDaySelected,
    getSortedAvailableTimeRanges,
} from '../../../helpers/formulaHelpers';

// hooks
import useFormulaTimeSlotsMutation from '../../../hooks/formulas/useFormulaTimeSlotMutation';

// models
import {
    FormulaDetailModel,
    FormulaSchedulingOptions,
    FormulaViewSetupFormValue,
} from '../../../models/formula.model';
import {
    TimingSlotBlock,
    TimingSlotsModel,
} from '../../../models/timing-slots.model';

interface StyledProps {
    isDisabled?: boolean;
    isHovered?: boolean;
}
interface MessageTimingConstraintsProps {
    timingSlots: TimingSlotsModel[];
    formula?: FormulaDetailModel;
    isDisabled?: boolean;
    schedulingType?: FormulaSchedulingOptions;
    formik?: FormikProps<FormulaViewSetupFormValue>;
}

interface MainBlockValues {
    id: string;
    isSelected: boolean;
}

enum SelectionType {
    Selected = 'selected',
    Deselected = 'deselected',
}

export interface ConstraintsFormValues {
    time_constraints: Record<string, Record<string, MainBlockValues>>;
}

const MessageTimingConstraints: FC<
    React.PropsWithChildren<MessageTimingConstraintsProps>
> = ({ timingSlots, formula, isDisabled, schedulingType, formik }) => {
    const [hoveredBlock, setHoveredBlock] = useState<TimingSlotBlock>({});
    const { mutate } = useFormulaTimeSlotsMutation(formula?.id as number);

    const availableTimeRanges: string[] = useMemo(
        () => getSortedAvailableTimeRanges(timingSlots),
        [timingSlots],
    );

    const initialValues: ConstraintsFormValues = useMemo(
        () =>
            getInitialConstraintsValues(
                availableTimeRanges,
                timingSlots,
                schedulingType !==
                    FormulaSchedulingOptions.AUTOMATED_WITH_GUARDRAILS
                    ? timingSlots.map((item) => item.id)
                    : formik
                    ? formik.values.time_slot_ids
                    : formula?.time_slot_ids,
            ),
        [availableTimeRanges, formik, formula, schedulingType, timingSlots],
    );

    const extendedWeekDays: string[] = useMemo(() => {
        const result: string[] = getAvailableAbbreviatedWeekDays(timingSlots);

        // to align the calendar we need a empty column at the start
        result.unshift(' ');
        return result;
    }, [timingSlots]);

    const handleSubmit = debounce((values: ConstraintsFormValues) => {
        const selectedIds = getConstraintsChanges(
            values,
            formula?.time_slot_ids,
        );

        if (formik) {
            formik.setFieldValue('time_slot_ids', selectedIds);
            return;
        }
        if (selectedIds) {
            mutate({
                time_slot_ids: selectedIds,
            });
        }
    }, 1000);

    const handleOnTimeSelect = (
        formikProps: FormikProps<ConstraintsFormValues>,
        time: string,
        allTimeSelected: boolean,
    ) => {
        extendedWeekDays.slice(1).forEach((day) => {
            formikProps.setFieldValue(
                `time_constraints.${day}.${time}.isSelected`,
                !allTimeSelected,
            );
        });
        formikProps.handleSubmit();
    };

    const handleOnDaySelect = (
        formikProps: FormikProps<ConstraintsFormValues>,
        day: string,
        allDaysSelected: boolean,
    ) => {
        availableTimeRanges.forEach((time) => {
            formikProps.setFieldValue(
                `time_constraints.${day}.${time}.isSelected`,
                !allDaysSelected,
            );
        });
        formikProps.handleSubmit();
    };

    const checkAllSelection = (
        formikProps: FormikProps<ConstraintsFormValues>,
        type: SelectionType,
    ): boolean =>
        Object.values(formikProps.values.time_constraints).every((day) => {
            return Object.values(day).every((time) => {
                if (type === SelectionType.Selected) {
                    return time.isSelected;
                } else {
                    return !time.isSelected;
                }
            });
        });

    const selectDeselectAllBlock = (
        formikProps: FormikProps<ConstraintsFormValues>,
    ) => {
        availableTimeRanges.forEach((time) =>
            handleOnTimeSelect(
                formikProps,
                time,
                checkAllSelection(formikProps, SelectionType.Selected),
            ),
        );
    };

    return (
        <Formik
            onSubmit={handleSubmit}
            initialValues={initialValues}
            enableReinitialize
        >
            {(formikProps: FormikProps<ConstraintsFormValues>) => (
                <Form>
                    <Container>
                        <Calendar>
                            {extendedWeekDays.map((day, index) => {
                                const isFirstColumn = index === 0;
                                const allDaysSelected =
                                    !isFirstColumn &&
                                    Object.keys(
                                        formikProps.values.time_constraints[
                                            day
                                        ],
                                    ).every(
                                        (time) =>
                                            formikProps.values.time_constraints[
                                                day
                                            ][time].isSelected,
                                    );
                                const isDayCheckBoxDisabled =
                                    allDaysSelected &&
                                    !getIsOtherDaySelected(
                                        formikProps.values,
                                        day,
                                    );
                                const isAllSelected = checkAllSelection(
                                    formikProps,
                                    SelectionType.Selected,
                                );
                                return (
                                    <Days key={day}>
                                        <Header>
                                            <Text
                                                isHovered={
                                                    !isDisabled &&
                                                    hoveredBlock?.day === day
                                                }
                                            >
                                                {day}
                                            </Text>
                                            {!isFirstColumn ? (
                                                <SelectAll
                                                    type={'checkbox'}
                                                    onChange={() =>
                                                        handleOnDaySelect(
                                                            formikProps,
                                                            day,
                                                            allDaysSelected,
                                                        )
                                                    }
                                                    checked={allDaysSelected}
                                                    disabled={
                                                        isDisabled ||
                                                        isDayCheckBoxDisabled
                                                    }
                                                    isDisabled={isDisabled}
                                                />
                                            ) : (
                                                !isDisabled && (
                                                    <SelectAllText
                                                        onClick={() =>
                                                            !isDisabled &&
                                                            !isAllSelected &&
                                                            selectDeselectAllBlock(
                                                                formikProps,
                                                            )
                                                        }
                                                        isDisabled={
                                                            isDisabled ||
                                                            isAllSelected
                                                        }
                                                    >
                                                        {'Select all'}
                                                    </SelectAllText>
                                                )
                                            )}
                                        </Header>
                                        {availableTimeRanges.map((time) => {
                                            const isSelected =
                                                !isFirstColumn &&
                                                formikProps.values
                                                    .time_constraints[day][time]
                                                    .isSelected;

                                            const allTimeSelected =
                                                Object.values(
                                                    formikProps.values
                                                        .time_constraints,
                                                )
                                                    .map((day) => day[time])
                                                    .every(
                                                        (time) =>
                                                            time.isSelected,
                                                    );

                                            const isMainBlockDisabled =
                                                isSelected &&
                                                getConstraintsChanges(
                                                    formikProps.values,
                                                )?.length === 1;
                                            const isTimeCheckBoxDisabled =
                                                allTimeSelected &&
                                                !getIsOtherTimeSelected(
                                                    formikProps.values,
                                                    time,
                                                );

                                            return (
                                                <Body key={time}>
                                                    {isFirstColumn && (
                                                        <BodyAction>
                                                            <Text
                                                                isHovered={
                                                                    !isDisabled &&
                                                                    hoveredBlock?.time ===
                                                                        time
                                                                }
                                                            >
                                                                {time}
                                                            </Text>
                                                            <SelectAll
                                                                type={
                                                                    'checkbox'
                                                                }
                                                                onChange={() =>
                                                                    handleOnTimeSelect(
                                                                        formikProps,
                                                                        time,
                                                                        allTimeSelected,
                                                                    )
                                                                }
                                                                checked={
                                                                    allTimeSelected
                                                                }
                                                                disabled={
                                                                    isDisabled ||
                                                                    isTimeCheckBoxDisabled
                                                                }
                                                                isDisabled={
                                                                    isDisabled
                                                                }
                                                            />
                                                        </BodyAction>
                                                    )}
                                                    {isMainBlockDisabled ? (
                                                        <AampeTooltip
                                                            arrow
                                                            title={
                                                                "All timeSlots can't be unselected"
                                                            }
                                                        >
                                                            <MainBlock
                                                                isFirstColumn={
                                                                    isFirstColumn
                                                                }
                                                                onClick={() => {
                                                                    const name = `time_constraints.${day}.${time}.isSelected`;

                                                                    if (
                                                                        !isDisabled &&
                                                                        !isMainBlockDisabled
                                                                    ) {
                                                                        formikProps.setFieldValue(
                                                                            name,
                                                                            !isSelected,
                                                                        );
                                                                        formikProps.handleSubmit();
                                                                    }
                                                                }}
                                                                isSelected={
                                                                    isSelected
                                                                }
                                                                isDisabled={
                                                                    isDisabled ||
                                                                    isMainBlockDisabled
                                                                }
                                                            >
                                                                {isSelected && (
                                                                    <ICONS.check
                                                                        size={
                                                                            24
                                                                        }
                                                                        color={
                                                                            aampeTheme
                                                                                .colors
                                                                                .success500
                                                                        }
                                                                    />
                                                                )}
                                                            </MainBlock>
                                                        </AampeTooltip>
                                                    ) : (
                                                        <MainBlock
                                                            isFirstColumn={
                                                                isFirstColumn
                                                            }
                                                            onClick={() => {
                                                                const name = `time_constraints.${day}.${time}.isSelected`;

                                                                if (
                                                                    !isDisabled &&
                                                                    !isMainBlockDisabled
                                                                ) {
                                                                    formikProps.setFieldValue(
                                                                        name,
                                                                        !isSelected,
                                                                    );
                                                                    formikProps.handleSubmit();
                                                                }
                                                            }}
                                                            onMouseEnter={() =>
                                                                setHoveredBlock(
                                                                    {
                                                                        day,
                                                                        time,
                                                                    },
                                                                )
                                                            }
                                                            onMouseLeave={() =>
                                                                setHoveredBlock(
                                                                    {},
                                                                )
                                                            }
                                                            isSelected={
                                                                isSelected
                                                            }
                                                            isDisabled={
                                                                isDisabled ||
                                                                isMainBlockDisabled
                                                            }
                                                        >
                                                            {isSelected && (
                                                                <ICONS.check
                                                                    size={24}
                                                                    color={
                                                                        aampeTheme
                                                                            .colors
                                                                            .success500
                                                                    }
                                                                />
                                                            )}
                                                        </MainBlock>
                                                    )}
                                                </Body>
                                            );
                                        })}
                                    </Days>
                                );
                            })}
                        </Calendar>
                    </Container>
                </Form>
            )}
        </Formik>
    );
};
export default MessageTimingConstraints;

const Calendar = styled.div(() => ({
    cursor: 'default',
    gap: aampeTheme.spacing(1),
    display: 'flex',
}));

const Header = styled.span(() => ({
    height: '30px',
    marginBottom: aampeTheme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(1),
}));

const Body = styled.div(() => ({
    display: 'flex',
    alignItems: 'center',
}));

const Container = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(2),
    background: aampeTheme.colors.white,
    padding: aampeTheme.spacing(2),
    width: 'fit-content',
    borderRadius: aampeTheme.borderRadius.default,
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
}));

const Days = styled.div(() => ({
    cursor: 'default',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: aampeTheme.spacing(2),
}));

const MainBlock = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        !['isFirstColumn', 'isSelected', 'isDisabled'].includes(prop),
})<{ isFirstColumn?: boolean; isSelected?: boolean; isDisabled?: boolean }>(
    ({ isFirstColumn, isSelected, isDisabled }) => ({
        border: !isFirstColumn
            ? `1px solid ${
                  isSelected
                      ? aampeTheme.colors.success500
                      : aampeTheme.colors.bordersWG01
              }`
            : undefined,
        borderRadius: aampeTheme.borderRadius.default,
        width: !isFirstColumn ? '32px' : '0px',
        height: '32px',
        backgroundColor: isSelected ? aampeTheme.colors.success100 : 'inherit',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: isFirstColumn ? aampeTheme.spacing(2) : undefined,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        '&:hover': {
            borderColor: !isDisabled ? aampeTheme.colors.solidA01 : undefined,
        },
    }),
);
const Text = styled.p.withConfig({
    shouldForwardProp: (prop) => prop !== 'isHovered',
})<{ isHovered?: boolean }>(({ isHovered }) => ({
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    color: isHovered ? aampeTheme.colors.solidA01 : aampeTheme.colors.gray700,
}));
const SelectAll = styled.input.withConfig({
    shouldForwardProp: (prop) => prop !== 'isDisabled',
})<StyledProps>(({ isDisabled }) => ({
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    '&:checked': {
        accentColor: aampeTheme.colors.solidA01,
    },
}));

const BodyAction = styled.div(() => ({
    display: 'flex',
    gap: aampeTheme.spacing(1),
    alignItems: 'center',
}));

const SelectAllText = styled(Text).withConfig({
    shouldForwardProp: (prop) => prop !== 'isDisabled',
})<StyledProps>(({ isDisabled }) => ({
    color: aampeTheme.colors.solidA01,
    textDecoration: 'underline',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
}));
